import { decryptItem, decryptItemRaw } from './crypto.js';
import { parseDropEnvelope } from './utils.js';

/**
 * Decrypt an item's encrypted_blob and parse the JSON envelope.
 * Handles both standard JSON envelopes and claimed-drop binary format.
 *
 * @param {object} item - Item with encrypted_blob, wrapped_key, nonce, item_type
 * @returns {{ envelope: object|null, dropBlob: Uint8Array|null, error: string|null }}
 */
export function decryptItemEnvelope(item) {
  if (item.item_type !== 'encrypted' || !item.encrypted_blob) {
    return { envelope: null, dropBlob: null, error: null };
  }

  try {
    const raw = decryptItem(
      item.encrypted_blob,
      item.wrapped_key,
      item.nonce,
      item.wrapped_key_format_version,
      item.encrypted_blob_format_version,
    );
    return { envelope: JSON.parse(raw), dropBlob: null, error: null };
  } catch (e) {
    // May be a claimed drop: binary format [4-byte meta len][JSON meta][file bytes]
    try {
      const rawBytes = decryptItemRaw(
        item.encrypted_blob,
        item.wrapped_key,
        item.nonce,
        item.wrapped_key_format_version,
        item.encrypted_blob_format_version,
      );
      const { meta, fileBytes } = parseDropEnvelope(rawBytes);
      const envelope = {
        name: meta.name,
        filename: meta.name,
        file_size: meta.size,
        mime_type: meta.type,
        item_type: 'file',
      };
      return { envelope, dropBlob: fileBytes, error: null };
    } catch (_) {
      return { envelope: null, dropBlob: null, error: e.message };
    }
  }
}

/**
 * Decrypt an item and return a display-friendly summary.
 * Used by items-list for card rendering.
 *
 * @param {object} item - Item with encrypted_blob, wrapped_key, nonce, item_type
 * @returns {{ name: string, item_type: string, isFile: boolean }}
 */
export function decryptItemSummary(item) {
  const { envelope } = decryptItemEnvelope(item);
  if (!envelope) {
    return { name: 'Untitled', item_type: item.item_type, isFile: false };
  }
  return {
    name: envelope.name || 'Untitled',
    item_type: envelope.item_type || item.item_type,
    isFile: !!envelope.filename,
  };
}

/**
 * Decrypt a group's encrypted_blob and return its name.
 *
 * @param {object} group - Group with encrypted_blob, wrapped_key, nonce
 * @returns {string}
 */
export function decryptGroupName(group) {
  try {
    const plaintext = decryptItem(group.encrypted_blob, group.wrapped_key, group.nonce);
    return JSON.parse(plaintext).name || 'Untitled';
  } catch {
    return 'Untitled';
  }
}
